import { createTheme, type Theme, type PaletteOptions } from '@mui/material/styles';

// Design tokens: two palette modes (light/dark) share geometry + typography.
// htmlFontSize:14 makes 1rem = 14px throughout.

const ACCENT = '#5E6AD2';      // Linear's signature violet-blue
const ACCENT_DARK = '#7C8AFF'; // brighter on near-black backgrounds

const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: { main: ACCENT, light: '#7C8AFF', dark: '#4F5AB3', contrastText: '#FFFFFF' },
  secondary: { main: '#6B7280', contrastText: '#FFFFFF' },
  success: { main: '#16A34A' },
  warning: { main: '#D97706' },
  error: { main: '#DC2626' },
  text: {
    primary: '#08090A',
    secondary: '#6B7280',
    disabled: '#9CA3AF',
  },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
  },
  divider: '#E5E7EB',
  action: {
    hover: 'rgba(8, 9, 10, 0.04)',
    selected: 'rgba(94, 106, 210, 0.08)',
    focus: 'rgba(94, 106, 210, 0.12)',
  },
};

const darkPalette: PaletteOptions = {
  mode: 'dark',
  primary: { main: ACCENT_DARK, light: '#A0ADFF', dark: '#5E6AD2', contrastText: '#08090A' },
  secondary: { main: '#9CA3AF', contrastText: '#08090A' },
  success: { main: '#22C55E' },
  warning: { main: '#FB923C' },
  error: { main: '#F87171' },
  text: {
    primary: '#F4F4F5',
    secondary: '#8B8B92',
    disabled: '#52525B',
  },
  background: {
    default: '#08090A',
    paper: '#1C1C1F',
  },
  divider: '#27272A',
  action: {
    hover: 'rgba(244, 244, 245, 0.04)',
    selected: 'rgba(124, 138, 255, 0.12)',
    focus: 'rgba(124, 138, 255, 0.16)',
  },
};

export function getTheme(mode: 'light' | 'dark'): Theme {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  return createTheme({
    palette,
    shape: { borderRadius: 6 },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      htmlFontSize: 14,
      h1: { fontSize: '2rem', fontWeight: 600, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em' },
      h3: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em' },
      h4: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontSize: '1rem', fontWeight: 600 },
      h6: { fontSize: '0.875rem', fontWeight: 600 },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8125rem' },
      caption: { fontSize: '0.75rem' },
      button: { fontWeight: 500, letterSpacing: 0 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            backgroundColor: palette.background?.default,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 6,
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 12,
            paddingRight: 12,
            fontWeight: 500,
            minHeight: 28,
            transition: 'background-color 80ms ease, color 80ms ease, border-color 80ms ease',
          },
          sizeSmall: { minHeight: 24, paddingTop: 3, paddingBottom: 3, fontSize: '0.75rem' },
          sizeLarge: { minHeight: 36, paddingTop: 8, paddingBottom: 8, fontSize: '0.875rem' },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: ({ theme }) => ({
            borderColor: theme.palette.divider,
          }),
        },
      },
      MuiIconButton: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: { borderRadius: 6 },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          }),
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 6,
            backgroundColor: theme.palette.background.paper,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
              transition: 'border-color 80ms ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? '#3F3F46' : '#9CA3AF',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: theme.palette.primary.main,
            },
          }),
          input: { paddingTop: 8, paddingBottom: 8 },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { fontSize: '0.875rem' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({ color: theme.palette.text.secondary }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.action.hover,
            padding: 2,
            borderRadius: 8,
            gap: 2,
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 500,
            border: 'none !important',
            borderRadius: '6px !important',
            color: theme.palette.text.secondary,
            paddingTop: 4,
            paddingBottom: 4,
            '&.Mui-selected': {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 0 0 1px rgba(255,255,255,0.06)'
                : '0 1px 2px 0 rgba(8,9,10,0.06)',
              '&:hover': { backgroundColor: theme.palette.background.paper },
            },
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 6 },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({ borderColor: theme.palette.divider }),
        },
      },
      MuiAppBar: {
        defaultProps: { color: 'default' },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.default,
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundImage: 'none',
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.mode === 'dark' ? '#27272A' : '#1C1C1F',
            color: '#F4F4F5',
            fontSize: '0.75rem',
            fontWeight: 500,
            padding: '4px 8px',
            borderRadius: 4,
          }),
          arrow: {
            color: '#1C1C1F',
          },
        },
      },
      MuiListItemButton: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            borderRadius: 6,
            paddingTop: 5,
            paddingBottom: 5,
            transition: 'background-color 80ms ease',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 16px 48px -8px rgba(0,0,0,0.6)'
              : '0 16px 48px -16px rgba(8,9,10,0.18)',
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 24px -4px rgba(0,0,0,0.6)'
              : '0 8px 24px -8px rgba(8,9,10,0.12)',
            backgroundImage: 'none',
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            height: 2,
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 40,
            color: theme.palette.text.secondary,
            '&.Mui-selected': { color: theme.palette.text.primary },
          }),
        },
      },
    },
  });
}

export const theme = getTheme('light');
